import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { MONTHS } from "./constants";
import IcArrDate from '@/assets/icons/ic-arr-date.svg'

interface MonthYearSelectorProps {
  month: number;
  year: number;
  onMonthChange: (m: number) => void;
  onYearChange: (y: number) => void;
}

export function MonthYearSelector({
  month,
  year,
  onMonthChange,
  onYearChange,
}: MonthYearSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleYearChange = (delta: number) => {
    onYearChange(year + delta);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-neutral/5"
      >
        <span className="text-h3 font-semibold text-neutral-strong">
          {MONTHS[month]} {year}
        </span>
        <img src={IcArrDate} alt="arr-date" />
      </button>

      {open && (
        <div className="absolute top-full left-0 z-50 mt-2 w-[280px] bg-white rounded-lg shadow-lg border border-neutral-light/20 p-4">
          {/* Year selector */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => handleYearChange(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral/5 hover:bg-neutral/10"
            >
              <ChevronLeft size={16} className="text-neutral-strong" />
            </button>
            <span className="text-h4 font-semibold text-neutral-strong">
              {year}
            </span>
            <button
              onClick={() => handleYearChange(1)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral/5 hover:bg-neutral/10"
            >
              <ChevronRight size={16} className="text-neutral-strong" />
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-4 gap-2">
            {MONTHS.map((m, idx) => (
              <button
                key={m}
                onClick={() => {
                  onMonthChange(idx);
                  setOpen(false);
                }}
                className={`
                  px-2 py-2 text-body rounded-lg text-center transition-colors
                  ${
                    idx === month
                      ? "bg-primary text-white"
                      : "hover:bg-primary text-neutral-strong"
                  }
                `}
              >
                {m.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
